import ReactGA from 'react-ga4';

// Initialize Google Analytics
export const initGA = () => {
  const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID || import.meta.env.REACT_APP_GA_MEASUREMENT_ID;
  
  if (measurementId) {
    ReactGA.initialize(measurementId, {
      gaOptions: {
        anonymizeIp: true, // Anonymize IP addresses for privacy
      },
    });
    console.log('Google Analytics initialized with ID:', measurementId);
  } else {
    console.warn('Google Analytics Measurement ID not found');
  }
};

// Track page views
export const trackPageView = (path) => {
  ReactGA.send({ hitType: 'pageview', page: path });
};

// Generic event tracking for GA4
// GA4 uses a different structure: event(eventName, eventParams)
export const trackEvent = (category, action, label = '', value = 0, customParams = {}) => {
  // For GA4, send as: ReactGA.event(action, { ...params })
  ReactGA.event(action, {
    event_category: category,
    event_label: label,
    value: value,
    ...customParams, // Custom parameters for GA4
  });
};

// Specific event tracking functions for better organization

// Landing Page Events
export const trackGetStartedClick = () => {
  trackEvent('Landing Page', 'Click', 'Get Started Button');
};

export const trackLoginClick = () => {
  trackEvent('Navigation', 'Click', 'Login Button');
};

export const trackAdminPanelClick = () => {
  trackEvent('Navigation', 'Click', 'Admin Panel Button');
};

// Scheme Interactions
export const trackSchemeClick = (schemeName, amcName) => {
  // Send with custom parameters for GA4 reporting
  trackEvent('Scheme', 'View Details', `${amcName} - ${schemeName}`, 0, {
    scheme_name: schemeName,
    amc_name: amcName,
    event_label: schemeName // GA4 event parameter
  });
};

export const trackSchemeModalOpen = (schemeName) => {
  trackEvent('Modal', 'Open', `Scheme Details - ${schemeName}`, 0, {
    scheme_name: schemeName,
    modal_type: 'scheme_details'
  });
};

export const trackSchemeModalClose = () => {
  trackEvent('Modal', 'Close', 'Scheme Details');
};

// Contact Events
export const trackContactModalOpen = (schemeName) => {
  trackEvent('Modal', 'Open', `Contact Form - ${schemeName}`, 0, {
    scheme_name: schemeName,
    modal_type: 'contact_form'
  });
};

export const trackContactModalClose = () => {
  trackEvent('Modal', 'Close', 'Contact Form');
};

export const trackContactFormSubmit = (schemeName, amcName) => {
  trackEvent('Contact', 'Submit', `${amcName} - ${schemeName}`, 0, {
    scheme_name: schemeName,
    amc_name: amcName,
    form_type: 'scheme_inquiry'
  });
};

export const trackTestEmailSend = (schemeName) => {
  trackEvent('Contact', 'Test Email', schemeName, 0, {
    scheme_name: schemeName
  });
};

// Compare Events
export const trackCompareScheme = (schemeName) => {
  trackEvent('Compare', 'Add Scheme', schemeName, 0, {
    scheme_name: schemeName,
    action_type: 'add'
  });
};

export const trackCompareModalOpen = (schemeCount) => {
  trackEvent('Modal', 'Open', `Compare - ${schemeCount} schemes`, 0, {
    scheme_count: schemeCount,
    modal_type: 'compare'
  });
};

export const trackCompareModalClose = () => {
  trackEvent('Modal', 'Close', 'Compare');
};

export const trackRemoveFromCompare = (schemeName) => {
  trackEvent('Compare', 'Remove Scheme', schemeName, 0, {
    scheme_name: schemeName,
    action_type: 'remove'
  });
};

// Weight Configuration Events
export const trackWeightModalOpen = () => {
  trackEvent('Modal', 'Open', 'Configure Weights');
};

export const trackWeightModalClose = () => {
  trackEvent('Modal', 'Close', 'Configure Weights');
};

export const trackWeightUpdate = (weights) => {
  const weightSummary = Object.entries(weights)
    .map(([key, value]) => `${key}:${value}`)
    .join(', ');
  trackEvent('Weights', 'Update', weightSummary);
};

export const trackWeightReset = () => {
  trackEvent('Weights', 'Reset', 'Default Weights');
};

// Fee Calculator Events
export const trackFeeCalculatorOpen = () => {
  trackEvent('Modal', 'Open', 'Fee Calculator');
};

export const trackFeeCalculatorClose = () => {
  trackEvent('Modal', 'Close', 'Fee Calculator');
};

export const trackFeeCalculation = (investmentAmount) => {
  trackEvent('Calculator', 'Calculate', 'Fee Calculation', investmentAmount);
};

// Search and Filter Events
export const trackSearch = (searchTerm) => {
  trackEvent('Search', 'Query', searchTerm);
};

export const trackFilter = (filterType, filterValue) => {
  trackEvent('Filter', filterType, filterValue);
};

export const trackSort = (sortColumn) => {
  trackEvent('Table', 'Sort', sortColumn);
};

export const trackPagination = (pageNumber) => {
  trackEvent('Table', 'Pagination', `Page ${pageNumber}`);
};

// Data Period Selection
export const trackDataPeriodChange = (period) => {
  trackEvent('Data', 'Period Change', period);
};

// Admin Events
export const trackAdminScrape = (pmName) => {
  trackEvent('Admin', 'Manual Scrape', pmName);
};

export const trackAdminBatchScrape = (count) => {
  trackEvent('Admin', 'Batch Scrape', `${count} PMs`);
};

export const trackAdminExport = (exportType) => {
  trackEvent('Admin', 'Export', exportType);
};

export const trackAdminClearCache = () => {
  trackEvent('Admin', 'Clear Cache', 'Backend Cache Cleared');
};

export const trackAdminConfigUpdate = (configType) => {
  trackEvent('Admin', 'Config Update', configType);
};

// Export/Download Events
export const trackDownload = (downloadType, fileName) => {
  trackEvent('Download', downloadType, fileName);
};

// Error Tracking
export const trackError = (errorType, errorMessage) => {
  trackEvent('Error', errorType, errorMessage);
};

// User Engagement Metrics
export const trackTimeOnPage = (pageName, timeInSeconds) => {
  trackEvent('Engagement', 'Time on Page', pageName, timeInSeconds);
};

export const trackScrollDepth = (percentage) => {
  trackEvent('Engagement', 'Scroll Depth', `${percentage}%`);
};
